import type { HTMLAttributes, ReactNode } from "react";

export type CardTone = "default" | "decision" | "success" | "warning" | "error";
export type LeftBorderTone = "success" | "warning" | "error" | "info" | "accent";

const TONE_CLASSES: Record<CardTone, string> = {
  default: "bg-card border-border",
  decision: "bg-decision border-accent/20",
  success: "bg-success-bg border-success/30",
  warning: "bg-warning-bg border-warning/30",
  error: "bg-error-bg border-error/30",
};

const LEFT_BORDER_CLASSES: Record<LeftBorderTone, string> = {
  success: "border-l-4 border-l-success",
  warning: "border-l-4 border-l-warning",
  error: "border-l-4 border-l-error",
  info: "border-l-4 border-l-info",
  accent: "border-l-4 border-l-accent",
};

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  tone?: CardTone;
  leftBorderTone?: LeftBorderTone;
  padding?: "none" | "sm" | "md" | "lg";
  children: ReactNode;
}

const PADDING_CLASSES = {
  none: "",
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
};

export function Card({
  tone = "default",
  leftBorderTone,
  padding = "md",
  className = "",
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={`rounded-lg border shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${TONE_CLASSES[tone]} ${leftBorderTone ? LEFT_BORDER_CLASSES[leftBorderTone] : ""} ${PADDING_CLASSES[padding]} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

interface CardSectionProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardHeader({ className = "", children, ...rest }: CardSectionProps) {
  return (
    <div className={`mb-3 flex items-start justify-between gap-3 ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function CardBody({ className = "", children, ...rest }: CardSectionProps) {
  return (
    <div className={className} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ className = "", children, ...rest }: CardSectionProps) {
  return (
    <div className={`mt-3 flex items-center justify-end gap-2 ${className}`} {...rest}>
      {children}
    </div>
  );
}
